import { useNavigate } from "react-router-dom";
import "../Management/NewCompanyProfile.css";
import React, { useState, useEffect } from "react";
import { ToastContainer, Zoom, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

/**
 * New Company Profile Component - Create company profile form
 * PROTECTED ROUTE: Requires user to be logged in
 */
export function NewCompanyProfile() {
  const navigate = useNavigate();
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Main company profile form data
  const [formData, setFormData] = useState({
    companyName: "",
    dba: "",
    fedraltaxid: "",
    industry: "",
    email: "",
    phone: "",
    website: "",
    address1: "",
    address2: "",
    city: "",
    zip: "",
    state: "",
    country: "",
    linkedin: "",
    facebook: "",
    insta: "",
    x: "",
    youtube: "",
    fontfamily: "",
    color1: "#ffffff",
    color2: "#ffffff", 
    color3: "#ffffff",
    logoUrl: "",
  });
  
  // Form validation states
  const [formErrors, setFormErrors] = useState({});
  const [wasValidated, setWasValidated] = useState(false);
  
  // Logo upload states
  const [preview, setPreview] = useState("https://i.ibb.co/4wrxz3pC/image-upload-icon.png");
  const [logoFile, setLogoFile] = useState(null);
  
  // State dropdown functionality
  const [input, setInput] = useState("");
  const [showList, setShowList] = useState(false);
  
  // Brand images upload state
  const [brandRelateImages, setBrandRelateImages] = useState({
    brandRelateImage1: null,
    brandRelateImage2: null,
    brandRelateImage3: null,
    brandRelateImage4: null,
    brandRelateImage5: null,
    brandRelateImage6: null,
  });

  // AUTHENTICATION CHECK: Redirect to login if not authenticated
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      // Not logged in -> redirect to login page
      toast.error("Please login to create a company profile", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      navigate("/login");
    }
  }, [navigate]);

  const usaStateCodes = [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ];

  // Filter states based on user input
  const filteredCities = usaStateCodes.filter((city) =>
    city.toLowerCase().startsWith(input.toLowerCase())
  );

  // Handle state selection from dropdown
  const handleSelect = (state) => {
    setFormData({ ...formData, state });
    setInput(state);
    setShowList(false);
  };

  // Handle logo file upload with validation and Azure upload
  const previewFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      toast.error("Only JPEG and PNG images are allowed.", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      event.target.value = "";
      setLogoFile(null);
      setPreview("https://i.ibb.co/4wrxz3pC/image-upload-icon.png");
      return;
    }

    setLogoFile(file); // Save the file for submission
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result); // Show a preview of the image
    };
    reader.readAsDataURL(file);

    try {
      const ext = file.name.split(".").pop();
      const newFileName = `company-logo.${ext}`;

      // Get a pre-signed upload URL from the server
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-upload-url`,
        {
          params: { fileName: newFileName },
        }
      );

      const uploadUrl = res.data.url;
      const imageUrlOnly = uploadUrl.split("?")[0];

      // Upload the file to Azure Blob Storage
      await axios.put(uploadUrl, file, {
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type,
        },
      });

      setPreview(imageUrlOnly); // Set preview to uploaded URL
      setFormData((prev) => ({ ...prev, logoUrl: imageUrlOnly })); // Add to formData
    } catch (err) {
      console.error("Image upload error:", err);
      // toast.error("Logo upload failed.");
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    const fieldMapping = {
      "cmpy-name": "companyName",
      "cmpy-dba": "dba",
      "cmpy-fedraltaxid": "fedraltaxid",
      "cmpy-industry": "industry",
      "cmpy-email": "email",
      "cmpy-phone": "phone",
      "cmpy-website": "website",
      "cmpy-address1": "address1",
      "cmpy-address2": "address2",
      "cmpy-city": "city",
      "cmpy-zip": "zip",
      "cmpy-state": "state",
      "cmpy-country": "country",
      "cmpy-linkedin": "linkedin",
      "cmpy-facebook": "facebook",
      "cmpy-insta": "insta",
      "cmpy-x": "x",
      "cmpy-youtube": "youtube",
      "cmpy-fontfamily": "fontfamily",
      "cmpy-color1": "color1",
      "cmpy-color2": "color2",
      "cmpy-color3": "color3",
    };

    setFormData({
      ...formData,
      [fieldMapping[id]]: value,
    });
  };

  // Handle brand image file uploads with validation
  const handleBrandImageChange = (event) => {
    const { id, files } = event.target;
    const file = files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png"];
      if (!validTypes.includes(file.type)) {
        toast.error("Only JPEG and PNG images are allowed.", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        // Optionally, clear the input
        event.target.value = "";
        setBrandRelateImages((prev) => ({
          ...prev,
          [id]: null,
        }));
        return;
      }
    }
    setBrandRelateImages((prev) => ({
      ...prev,
      [id]: file || null,
    }));
  };

  // Submit company profile form with validation
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    setWasValidated(true);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill in all required fields", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("You must be logged in to create a company profile", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        navigate("/login");
        return;
      }

      // Prepare form data
      const formDataToSubmit = new FormData();
      formDataToSubmit.append("companyName", formData.companyName);
      formDataToSubmit.append("dba", formData.dba);
      formDataToSubmit.append("fedraltaxid", formData.fedraltaxid);
      formDataToSubmit.append("industry", formData.industry);
      formDataToSubmit.append("email", formData.email);
      formDataToSubmit.append("phone", formData.phone);
      formDataToSubmit.append("website", formData.website);
      formDataToSubmit.append("address1", formData.address1);
      formDataToSubmit.append("address2", formData.address2);
      formDataToSubmit.append("city", formData.city);
      formDataToSubmit.append("zip", formData.zip);
      formDataToSubmit.append("state", formData.state);
      formDataToSubmit.append("country", formData.country);
      formDataToSubmit.append("linkedin", formData.linkedin);
      formDataToSubmit.append("facebook", formData.facebook);
      formDataToSubmit.append("insta", formData.insta);
      formDataToSubmit.append("x", formData.x);
      formDataToSubmit.append("youtube", formData.youtube);
      formDataToSubmit.append("fontfamily", formData.fontfamily);
      formDataToSubmit.append("color1", formData.color1);
      formDataToSubmit.append("color2", formData.color2);
      formDataToSubmit.append("color3", formData.color3);

      // Add logo to the form data
      if (logoFile) {
        // If a file is uploaded, append it to the form data
        formDataToSubmit.append("logo", logoFile);
      } else if (formData.logoUrl) {
        // If a logo URL is provided, append it as a string
        formDataToSubmit.append("logoUrl", formData.logoUrl);
      }

      // Add brand images to the form data (optional)
      Object.entries(brandRelateImages).forEach(([key, file]) => {
        if (file) {
          formDataToSubmit.append(key, file);
        }
      });

      // Send the request to the server
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/company/update-company-profile`,
        formDataToSubmit,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data", // Important for file uploads
          },
        }
      );

      toast.success("Company profile created successfully!", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      setTimeout(() => navigate("/management", { state: { fromNewCompany: true } }), 2000);
    } catch (err) {
      console.error("Profile submission error:", err);
      toast.error(
        err.response?.data?.message || "Failed to create company profile",
        {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validate required form fields
  const validateForm = () => {
    const errors = {};
    if (!formData.companyName.trim())
      errors.companyName = "Company name is required";
    if (!formData.industry.trim()) errors.industry = "Industry is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!formData.phone.trim()) errors.phone = "Phone number is required";
    return errors;
  };

  // Get placeholder text with error message if validation failed
  const getPlaceholder = (fieldName, defaultText) => {
    // If field has been validated and has an error, show the error message
    if (wasValidated && formErrors[fieldName]) {
      return formErrors[fieldName];
    }
    // Otherwise show the default placeholder
    return defaultText;
  };

  return (
    <div className="new-cmpy">
      <ToastContainer
        position="top-right"
        autoClose={500}
        hideProgressBar={false}
        newestOnTop={true}
        transition={Zoom}
      />
      <div className="new-company-name">Create New Company Profile</div>
      <div className="new-cmpy-form-conatiner">
        <form className="new-cmpy-form" onSubmit={handleSubmit}>
          <div className="for-1st-row">
            <input
              type="text"
              id="cmpy-name"
              placeholder={getPlaceholder(
                "companyName",
                "Company Name (Require)"
              )}
              onChange={handleInputChange}
              value={formData.companyName}
              className={formErrors.companyName && wasValidated ? "error" : ""}
            />
            <div className="new-cmpny-input-logo-sec">
              <div className="col-img">
                <label htmlFor="cmpy-logo" className="logo-label">
                  Company Logo <span className="red-imp">*</span>
                </label>
                <input
                  className="logo-input"
                  type="file"
                  onChange={previewFile}
                  accept="image/*"
                  id="cmpy-logo"
                />
              </div>
              <div className="logo-preview">
                <img src={preview} height="200" alt="Image preview..." />
              </div>
            </div>
          </div>
          <div className="new-cmpny-input-sec req">
            <input
              type="text"
              id="cmpy-dba"
              placeholder={getPlaceholder("dba", "DBA")}
              onChange={handleInputChange}
              value={formData.dba}
            />

            <input
              type="text"
              id="cmpy-fedraltaxid"
              placeholder={getPlaceholder("fedraltaxid", "Federal Tax ID/EIN")}
              onChange={handleInputChange}
              value={formData.fedraltaxid}
            />

            <input
              type="text"
              id="cmpy-industry"
              placeholder={getPlaceholder("industry", "Industry (Require)")}
              onChange={handleInputChange}
              value={formData.industry}
              className={formErrors.industry && wasValidated ? "error" : ""}
            />

            <input
              type="email"
              id="cmpy-email"
              placeholder={getPlaceholder("email", "Company Email (Require)")}
              onChange={handleInputChange}
              value={formData.email}
              className={formErrors.email && wasValidated ? "error" : ""}
            />

            <input
              type="tel"
              maxLength={13}
              id="cmpy-phone"
              placeholder={getPlaceholder("phone", "Company Phone (Require)")}
              onChange={handleInputChange}
              value={formData.phone}
              className={formErrors.phone && wasValidated ? "error" : ""}
            />

            <input
              type="url"
              id="cmpy-website"
              placeholder="Company Website"
              onChange={handleInputChange}
              value={formData.website}
            />

            <input
              type="text"
              id="cmpy-address1"
              placeholder="Company Address 1"
              onChange={handleInputChange}
              value={formData.address1}
            />
            <input
              type="text"
              id="cmpy-address2"
              placeholder="Company Address 2"
              onChange={handleInputChange}
              value={formData.address2}
            />
            <input
              type="text"
              id="cmpy-city"
              placeholder="City"
              onChange={handleInputChange}
              value={formData.city}
            />

            <input
              type="text"
              id="cmpy-zip"
              placeholder="Zip Code"
              onChange={handleInputChange}
              value={formData.zip}
            />
            <div className="city-list">
              <input
                type="text"
                id="cmpy-state"
                value={formData.state}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, state: value });
                  setInput(value);
                  setShowList(true);
                }}
                onFocus={() => setShowList(true)}
                onBlur={() => setTimeout(() => setShowList(false), 150)}
                placeholder="State"
                autoComplete="off"
              />

              {showList && filteredCities.length > 0 && (
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    minWidth: "50%",
                    maxHeight: "220px",
                    overflowY: "auto",
                    border: "0.5px solid #ccc",
                    backgroundColor: "white",
                    zIndex: 1000,
                  }}
                >
                  {filteredCities.map((city) => (
                    <li
                      key={city}
                      onMouseDown={() => handleSelect(city)}
                      style={{ padding: "8px", cursor: "pointer" }}
                    >
                      {city}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <input
              type="text"
              id="cmpy-country"
              placeholder="Country"
              onChange={handleInputChange}
              value={formData.country}
            />
          </div>

          <div className="cmpy-brand-title">Social Platforms Details</div>

          <div className="new-cmpny-input-sec">
            <input
              type="text"
              id="cmpy-linkedin"
              placeholder="LinkedIn"
              onChange={handleInputChange}
              value={formData.linkedin}
            />
            <input
              type="text"
              id="cmpy-facebook"
              placeholder="Facebook"
              onChange={handleInputChange}
              value={formData.facebook}
            />
            <input
              type="text"
              id="cmpy-insta"
              placeholder="Instagram"
              onChange={handleInputChange}
              value={formData.insta}
            />
            <input
              type="text"
              id="cmpy-x"
              placeholder="X"
              onChange={handleInputChange}
              value={formData.x}
            />
            <input
              type="text"
              id="cmpy-youtube"
              placeholder="YouTube"
              onChange={handleInputChange}
              value={formData.youtube}
            />
          </div>

          <div className="cmpy-brand-title">
            Company Brand Details{" "}
            <span>
              (Brand Logos, Website/Promo Banner, Product or Service Image, etc)
            </span>
          </div>
          <div className="new-cmpny-input-brand-sec">
            <div className="brand-col-img">
              <label htmlFor="brandRelateImage1">Brand Relate Image 1</label>
              <input
                className="logo-input"
                type="file"
                onChange={handleBrandImageChange}
                accept="image/*"
                id="brandRelateImage1"
              />
            </div>
            <div className="brand-col-img">
              <label htmlFor="brandRelateImage2">Brand Relate Image 2</label>
              <input
                className="logo-input"
                type="file"
                onChange={handleBrandImageChange}
                accept="image/*"
                id="brandRelateImage2"
              />
            </div>
            <div className="brand-col-img">
              <label htmlFor="brandRelateImage3">Brand Relate Image 3</label>
              <input
                className="logo-input"
                type="file"
                onChange={handleBrandImageChange}
                accept="image/*"
                id="brandRelateImage3"
              />
            </div>
            <div className="brand-col-img">
              <label htmlFor="brandRelateImage4">Brand Relate Image 4</label>
              <input
                className="logo-input"
                type="file"
                onChange={handleBrandImageChange}
                accept="image/*"
                id="brandRelateImage4"
              />
            </div>
            <div className="brand-col-img">
              <label htmlFor="brandRelateImage5">Brand Relate Image 5</label>
              <input
                className="logo-input"
                type="file"
                onChange={handleBrandImageChange}
                accept="image/*"
                id="brandRelateImage5"
              />
            </div>

            <div className="brand-col-img">
              <label htmlFor="brandRelateImage6">Brand Relate Image 6</label>
              <input
                className="logo-input"
                type="file"
                onChange={handleBrandImageChange}
                accept="image/*"
                id="brandRelateImage6"
              />
            </div>
            <div className="brand-row">
              <input
                type="text"
                id="cmpy-fontfamily"
                placeholder="Font Family"
                className="brand-input"
                onChange={handleInputChange}
                value={formData.fontfamily}
              />
            </div>
            <div className="brand-row">
              <label htmlFor="cmpy-color1">Color1</label>
              <input
                className="color-input"
                type="color"
                id="cmpy-color1"
                onChange={handleInputChange}
                value={formData.color1}
              />
            </div>
            <div className="brand-row">
              <label htmlFor="cmpy-color2">Color2</label>
              <input
                className="color-input"
                type="color"
                id="cmpy-color2"
                onChange={handleInputChange}
                value={formData.color2}
              />
            </div>
            <div className="brand-row">
              <label htmlFor="cmpy-color3">Color3</label>
              <input
                className="color-input"
                type="color"
                id="cmpy-color3"
                placeholder="Color Pallete Color 3 (Optional)"
                onChange={handleInputChange}
                value={formData.color3}
              />
            </div>
          </div>

          <div className="row-input-fields">
            <button
              type="submit"
              className="new-cmpy-button"
              disabled={isSubmitting}
              style={{ backgroundColor: formData.color1 }}
            >
              {isSubmitting ? "Creating..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
